import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';
import { Observable } from 'rxjs';

export interface ProductivityPeriod {
  periodId: string;
  label: string;
  kind: 'MONTH' | 'SPRINT' | 'PI';
  startDate: string;
  endDate: string;
  sortOrder: number;
  isActive: boolean;
}

export interface ProductivityMetric {
  metricId: string;
  name: string;
  description: string | null;
  scope: 'PERSON' | 'TEAM';
  role: string | null;
  unit: string | null;
  direction: 'HIGHER_BETTER' | 'LOWER_BETTER';
  sortOrder: number;
  isActive: boolean;
}

export interface ProductivityTeamPreview {
  teamName: string;
  teamKey: string;
  status: 'EXISTING' | 'NEW' | 'FUZZY_MATCH';
  suggestedTeamName?: string;
  suggestedTeamKey?: string;
  memberCount?: number;
  rowsInFile?: number;
  /** Current LOB of the team (or of the suggested existing team), if already assigned. */
  lobId?: number | null;
  lobName?: string | null;
}

export interface ProductivityRowPreview {
  rowNumber: number;
  teamName: string;
  teamKey: string;
  scope: 'PERSON' | 'TEAM';
  resourceName: string | null;
  employeeId: string | null;
  metric: string | null;
  metricId: string | null;
  baseline: number | null;
  actual: number | null;
  notes: string | null;
  status: 'NEW' | 'UPDATED' | 'WARNING' | 'ERROR';
  messages: string[];
}

export interface ProductivityPreviewResponse {
  batchId: string;
  periodId: string;
  fileName: string;
  totalRows: number;
  newRows: number;
  updatedRows: number;
  warningRows: number;
  errorRows: number;
  teams: ProductivityTeamPreview[];
  rows: ProductivityRowPreview[];
}

export interface ProductivityCommitResponse {
  batchId: string;
  status: 'COMMITTED' | 'FAILED';
  insertedRows: number;
  updatedRows: number;
  skippedRows: number;
  message: string;
}

export interface ProductivityRecentImport {
  batchId: string;
  fileName: string | null;
  periodId: string;
  periodLabel: string;
  uploadedBy: string;
  uploadedAt: string;
  committedAt: string | null;
  status: 'PREVIEW' | 'COMMITTED' | 'ROLLED_BACK' | 'FAILED';
  rowCounts: string | null;
}

export interface ProductivityTeamOption {
  teamKey: string;
  teamName: string;
  lobId: number | null;
  lobName: string | null;
}

/** One LOB with the teams visible to the caller; lobId null = "Unassigned" bucket. */
export interface ProductivityLobOption {
  lobId: number | null;
  lobName: string;
  teams: ProductivityTeamOption[];
}

/** Selector value for an LOB: numeric id, or 'unassigned' for teams without one. */
export type ProductivityLobKey = number | 'unassigned';

export interface ProductivityRecord {
  id: number;
  scope: 'PERSON' | 'TEAM';
  employeeId: string | null;
  employeeName: string | null;
  teamName: string;
  teamKey: string;
  metricId: string;
  metricName: string;
  periodId: string;
  baseline: number | null;
  actual: number | null;
  notes: string | null;
  uploadedAt: string;
}

@Injectable({ providedIn: 'root' })
export class ProductivityService {
  private readonly base = `${environment.apiUrl}/productivity`;

  constructor(private readonly http: HttpClient) {}

  listPeriods(): Observable<ProductivityPeriod[]> {
    return this.http.get<ProductivityPeriod[]>(`${this.base}/periods`);
  }

  listMetrics(): Observable<ProductivityMetric[]> {
    return this.http.get<ProductivityMetric[]>(`${this.base}/metrics`);
  }

  preview(file: File, periodId: string): Observable<ProductivityPreviewResponse> {
    const form = new FormData();
    form.append('file', file);
    form.append('periodId', periodId);
    return this.http.post<ProductivityPreviewResponse>(`${this.base}/imports/preview`, form);
  }

  commit(
    batchId: string,
    file: File,
    teamOverrides: Record<string, string>,
    teamLobs: Record<string, number> = {},
  ): Observable<ProductivityCommitResponse> {
    const form = new FormData();
    form.append('file', file);
    Object.entries(teamOverrides).forEach(([from, to]) => {
      form.append(`teamOverrides[${from}]`, to);
    });
    Object.entries(teamLobs).forEach(([teamKey, lobId]) => {
      form.append(`teamLobs[${teamKey}]`, String(lobId));
    });
    return this.http.post<ProductivityCommitResponse>(
      `${this.base}/imports/${batchId}/commit`,
      form,
    );
  }

  recentImports(limit = 10): Observable<ProductivityRecentImport[]> {
    return this.http.get<ProductivityRecentImport[]>(
      `${this.base}/imports/recent?limit=${limit}`,
    );
  }

  rowsForBatch(batchId: string): Observable<ProductivityRecord[]> {
    return this.http.get<ProductivityRecord[]>(
      `${this.base}/imports/${batchId}/rows`,
    );
  }

  downloadTemplate(): Observable<Blob> {
    return this.http.get(`${this.base}/imports/template`, { responseType: 'blob' });
  }

  recordsForEmployee(employeeId: string, monthsBack = 6): Observable<ProductivityRecord[]> {
    return this.http.get<ProductivityRecord[]>(
      `${this.base}/employees/${encodeURIComponent(employeeId)}/records?monthsBack=${monthsBack}`,
    );
  }

  listTeams(): Observable<ProductivityTeamOption[]> {
    return this.http.get<ProductivityTeamOption[]>(`${this.base}/teams`);
  }

  recordsForTeam(teamKey: string, monthsBack = 12): Observable<ProductivityRecord[]> {
    return this.http.get<ProductivityRecord[]>(
      `${this.base}/teams/${encodeURIComponent(teamKey)}/records?monthsBack=${monthsBack}`,
    );
  }

  /** Records across an explicit set of teams (multi-select). */
  recordsForTeams(teamKeys: string[], monthsBack = 12): Observable<ProductivityRecord[]> {
    const params = teamKeys.map((k) => `teamKeys=${encodeURIComponent(k)}`).join('&');
    return this.http.get<ProductivityRecord[]>(
      `${this.base}/teams/records?${params}&monthsBack=${monthsBack}`,
    );
  }

  /** Excel export of the dashboard for the selected teams. */
  exportExcel(teamKeys: string[], monthsBack = 12): Observable<Blob> {
    const params = teamKeys.map((k) => `teamKeys=${encodeURIComponent(k)}`).join('&');
    return this.http.get(`${this.base}/export.xlsx?${params}&monthsBack=${monthsBack}`, { responseType: 'blob' });
  }

  /** LOBs with the teams the caller may see (plus an "Unassigned" bucket when needed). */
  listLobs(): Observable<ProductivityLobOption[]> {
    return this.http.get<ProductivityLobOption[]>(`${this.base}/lobs`);
  }

  /** Every LOB, for pickers. Teams array is empty here. */
  lobCatalog(): Observable<ProductivityLobOption[]> {
    return this.http.get<ProductivityLobOption[]>(`${this.base}/lobs/catalog`);
  }

  /** Records across every visible team in one LOB. */
  recordsForLob(lob: ProductivityLobKey, monthsBack = 12): Observable<ProductivityRecord[]> {
    return this.http.get<ProductivityRecord[]>(
      `${this.base}/lobs/${encodeURIComponent(String(lob))}/records?monthsBack=${monthsBack}`,
    );
  }

  /** Assign or change a team's LOB. */
  setTeamLob(teamKey: string, lobId: number): Observable<ProductivityTeamOption> {
    return this.http.put<ProductivityTeamOption>(
      `${this.base}/teams/${encodeURIComponent(teamKey)}/lob`,
      { lobId },
    );
  }
}
